import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ShieldCheck, MailCheck, XCircle } from "lucide-react";
import { passwordError, PW_HINT } from "@/lib/password";

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState("checking"); // checking | valid | invalid | done
  const [email, setEmail] = useState("");
  const [invalidMsg, setInvalidMsg] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/invites/validate/${token}`)
      .then((res) => { setEmail(res.data.username); setState("valid"); })
      .catch((e) => { setInvalidMsg(formatApiErrorDetail(e.response?.data?.detail) || "This invite link is not valid."); setState("invalid"); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const pErr = passwordError(pw);
    if (pErr) return setError(pErr);
    if (pw !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await axios.post(`${API}/invites/accept/${token}`, { password: pw });
      setState("done");
      toast.success("Account created — sign in to set up your authenticator");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative h-screen w-full flex items-center justify-center bg-background text-foreground p-4 scanlines">
      <div className="w-full max-w-sm border border-border bg-surface">
        <div className="border-b border-border px-6 py-6 flex flex-col items-center gap-3 bg-black">
          <img src="/soverenx-logo.png" alt="SoverenX Systems" className="h-14 w-auto object-contain" />
          <div className="text-center">
            <h1 className="text-sm font-semibold tracking-tight leading-none">SoverenX Scrubber</h1>
            <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-1">Accept Invitation</p>
          </div>
        </div>

        {state === "checking" && (
          <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        )}

        {state === "invalid" && (
          <div className="p-6 text-center space-y-4" data-testid="invite-invalid">
            <XCircle className="h-8 w-8 text-red-400 mx-auto" />
            <p className="text-sm text-muted-foreground">{invalidMsg}</p>
            <Link to="/login"><Button variant="outline" className="rounded-none border-border">Go to Login</Button></Link>
          </div>
        )}

        {state === "valid" && (
          <form onSubmit={submit} className="px-6 py-6 space-y-4" data-testid="invite-form">
            <p className="text-xs text-muted-foreground">
              Set a password for <span className="text-foreground font-mono">{email}</span>. You'll set up Google/Microsoft Authenticator 2FA when you first sign in.
            </p>
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Password</Label>
              <Input data-testid="invite-password-input" type="password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)} required
                className="font-mono rounded-none bg-background border-border focus-visible:ring-primary" />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Confirm Password</Label>
              <Input data-testid="invite-confirm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required
                className="font-mono rounded-none bg-background border-border focus-visible:ring-primary" />
            </div>
            <p className="text-[10px] text-muted-foreground">{PW_HINT}</p>
            {error && <p data-testid="invite-error" className="text-xs font-mono text-red-400 border border-red-900/60 bg-red-950/30 px-3 py-2">{error}</p>}
            <Button type="submit" data-testid="invite-submit" disabled={loading}
              className="w-full rounded-none bg-primary text-primary-foreground hover:bg-accent-hover font-medium">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4 mr-2" /> Create Account</>}
            </Button>
          </form>
        )}

        {state === "done" && (
          <div className="p-6 text-center space-y-4" data-testid="invite-done">
            <MailCheck className="h-8 w-8 text-green-400 mx-auto" />
            <p className="text-sm text-muted-foreground">Your account for <span className="text-foreground font-mono">{email}</span> is ready. Sign in and set up your authenticator app.</p>
            <Button data-testid="invite-goto-login" onClick={() => navigate("/login")} className="rounded-none bg-primary text-primary-foreground hover:bg-accent-hover font-medium">Go to Login</Button>
          </div>
        )}

        <div className="border-t border-border px-6 py-3 text-[10px] uppercase tracking-[0.18em] text-muted-foreground text-center">
          Files are scrubbed locally — never uploaded
        </div>
      </div>
    </div>
  );
}
